import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import { Tooltip } from 'devextreme-react/tooltip';
import { IValuesCollection } from '../ts/appTheme';
import { TitlePositionType } from '../ts/Interfaces';

import ParameterFieldBase, {
    IParameterFieldBase,
    IParameterFieldBaseOptions
} from '../../../_common/platform/ts/ParameterFieldBase';

let inlineHelpID = 0;

interface IChildProps {
    value?: any;
    param?: ParameterFieldBase<IParameterFieldBaseOptions>;
    // Some child compenents may have a theme specified as themeObj or theme.
    themeObj?: IValuesCollection;
    theme?: IValuesCollection;

    onChange?(value: any): void;
}

export function ParameterField(props: {
    className?: string;
    widgetId?: number;
    widgetType?: string;
    fieldName?: string;
    checked?: boolean;
    grayedOut?: boolean;
    title?: string;
    errors?: string[];
    showErrorsAsText?: boolean;
    autofocus?: string;
    param?: IParameterFieldBase<IParameterFieldBaseOptions>;
    children?: React.ReactNode;

    label?: string;
    selectMode?: boolean;
    Layout?;
    hidden?: boolean;
    noTitle?: boolean;
    titlePosition?: TitlePositionType;
    titleWidth?: number;
    verticalSpaceBetweenFields?: number;

    locked?: boolean;
    showLocked?: boolean;

    clearable?: boolean;
    showInheritedIcon?: boolean;
    defaultValue?: any;
    showClearIcon?: boolean; //Is used when child element has no 'param' property.

    options?: {
        inlineHelp?: string;
        noTitle?: boolean;
        inline?: boolean;
        inlineFixed?: number;
        clearable?: boolean;
        showInheritedIcon?: boolean;
        defaultValue?: any;
        height?: string;

        onClear?(): void;
    };
    glob?: IGlob;

    onClick?(): void;
    onSelect?(fieldName: string): void;
    onClear?(): void;
}) {
    React.useEffect(() => {
        $(function () {
            $('input:visible:enabled:first').focus(0);
        });
    }, []);

    const handleSelect = (e) => {
        if (typeof props.onSelect === 'function') {
            props.onSelect(props.fieldName);
        }
    };

    const getChildProps = (child: any): IChildProps => {
        if (child != null) {
            if (Array.isArray(child)) {
                if (child.length === 1) {
                    return child[0].props;
                }
            } else {
                return child.props;
            }
        }

        return null;
    };

    const handleOnClearClick = (e) => {
        if (typeof props.onClear === 'function') {
            props.onClear();
        } else if (typeof props.options.onClear === 'function') {
            props.options.onClear();
        } else {
            const childProps = getChildProps(props.children);

            if (childProps != null && typeof childProps.onChange === 'function') {
                const defaultValue = getDefaultValue(props.param, childProps.themeObj || childProps.theme);

                childProps.onChange(defaultValue);
            }
        }
    };

    const getDefaultValue = (param: IParameterFieldBase<IParameterFieldBaseOptions>, theme: IValuesCollection) => {
        if (props.defaultValue !== undefined) {
            return props.defaultValue;
        }

        if (props.options != null && props.options.defaultValue !== undefined) {
            return props.options.defaultValue;
        }

        if (param == null) {
            return undefined;
        }

        if (typeof param.getDefault === 'function') {
            return param.getDefault(theme);
        }

        if (typeof param.defaultVal === 'function') {
            return param.defaultVal(theme);
        } else {
            return param.defaultVal;
        }
    };

    const { param, verticalSpaceBetweenFields } = props;

    let errors: string[] = [];

    if (props.errors != null) {
        errors = errors.concat(_.compact(props.errors));
    }

    if (param != null && param.errors != null) {
        errors = errors.concat(_.compact(param.errors));
    }

    let errorsElement: React.JSX.Element;
    let errorIcon: React.JSX.Element;

    if (errors.length > 0) {
        if (props.showErrorsAsText) {
            errorsElement = (
                <div className="errors">
                    <span>{errors.join(', ')}</span>
                </div>
            );
        } else {
            errorIcon = (
                <div className="error-mark">
                    <span className="dx-icon dx-icon-clear" title={errors.join(', ')} />
                </div>
            );
        }
    }

    let lockButton: React.JSX.Element;

    if (props.showLocked || props.locked) {
        lockButton = (
            <span
                className="glyphicon glyphicon-paperclip locked-indicator"
                onClick={() => {
                    props.glob.AppActions.templateSetLock(
                        props.widgetId,
                        props.widgetType,
                        props.fieldName,
                        !props.locked
                    );
                }}
            />
        );
    }

    let checkedIcon: React.JSX.Element;

    if (props.checked) {
        checkedIcon = <span className="glyphicon glyphicon-ok checked-icon"></span>;
    }

    const options = props.options ?? {};

    const labelStyle: React.CSSProperties = {
        width: typeof options.inlineFixed === 'number' ? options.inlineFixed + 'px' : 'auto'
    };

    let inlineHelp: React.JSX.Element;

    if (options.inlineHelp != null) {
        inlineHelp = (
            <td className="parameter-field-inline-help-cell">
                <div
                    id={'infoSign_' + inlineHelpID}
                    className="glyphicon glyphicon-question-sign"
                    //title={ options.inlineHelp }
                />
                <Tooltip
                    target={'#infoSign_' + inlineHelpID}
                    showEvent="dxhoverstart"
                    hideEvent="dxhoverend"
                    contentRender={() => {
                        return <div>{options.inlineHelp}</div>;
                    }}
                />
            </td>
        );

        inlineHelpID++;
    }

    const titlePosition = props.titlePosition || 'above';

    let labelElement: React.JSX.Element;

    if (props.label != null && props.label.length > 0) {
        labelElement = <label style={labelStyle}>{props.label}</label>;
    } else {
        let title = props.title;
        let mandatoryMark: React.JSX.Element;

        // The field can be explicitly marked as [mandatory] by suffixing with *
        // Such validations are to be performed at backend.
        if (param != null) {
            if (title == null && param.title != null) {
                title = param.title.endsWith('*') ? param.title.replace('*', '') : param.title;
            }

            if (
                (param.options != null && param.options.mandatory) ||
                (param.title != null && param.title.endsWith('*'))
            ) {
                mandatoryMark = <span className="mandatory-mark">*</span>;
            }
        }

        if (props.noTitle !== true && options.noTitle !== true) {
            if (
                lockButton != null ||
                checkedIcon != null ||
                mandatoryMark != null ||
                (title != null && title.length > 0)
            ) {
                labelElement = (
                    <label style={labelStyle}>
                        {lockButton}
                        {checkedIcon}

                        <span>{title}</span>
                        {mandatoryMark}
                    </label>
                );
            }
        } else if (titlePosition === 'above') {
            labelElement = mandatoryMark || <span> </span>;
        } else if (mandatoryMark != null && titlePosition === 'before') {
            labelElement = <div style={{ float: 'inline-end' }}>{mandatoryMark}</div>;
        }
    }

    let overlay: React.JSX.Element;

    if (props.selectMode) {
        overlay = (
            <div className="select-overlay" onClick={handleSelect}>
                <div className="overlay-inner"></div>
            </div>
        );
    }

    if (props.Layout != null) {
        const Layout = props.Layout;

        return <Layout label={labelElement} children={props.children} errors={errorIcon} />;
    }

    const children = props.children;

    let childElements = null;

    if (children != null) {
        if (Array.isArray(children)) {
            const elements = [];

            children.forEach((child) => {
                if (child != null) {
                    elements.push(child);
                }
            });

            if (elements.length > 0) {
                childElements = elements;
            }
        } else {
            childElements = children;
        }
    }

    let clearable = props.clearable || options.clearable;
    let clearButton: React.JSX.Element;

    if (clearable) {
        if (props.showClearIcon !== undefined) {
            clearable = props.showClearIcon;
        } else {
            const childProps = getChildProps(childElements);

            if (childProps != null) {
                const defaultValue = getDefaultValue(childProps.param, childProps.themeObj || childProps.theme);

                if (
                    childProps.value == null ||
                    defaultValue === undefined ||
                    (childProps.param != null && typeof childProps.param.isEqual === 'function'
                        ? childProps.param.isEqual(childProps.value, defaultValue)
                        : childProps.value === defaultValue)
                ) {
                    clearable = false;
                }
            }
        }

        if (clearable) {
            clearButton = (
                <div
                    key="clear-button"
                    className="glyphicon glyphicon-remove clear-button"
                    onClick={handleOnClearClick}
                ></div>
            );
        }
    }

    const childElementsBlock: React.JSX.Element = (
        <div className={'child-elements' + (props.grayedOut ? ' grayed-out' : '')}>
            {errorsElement}
            {childElements}
            {errorIcon}
        </div>
    );

    let inheritedIcon: React.JSX.Element;

    if (props.showInheritedIcon || options.showInheritedIcon) {
        inheritedIcon = (
            <div
                key="inherited-icon"
                className="glyphicon glyphicon-arrow-down inherited-icon"
                title={'Inherited from parent theme'}
            ></div>
        );
    }

    let titleElement: React.JSX.Element;

    if (
        (titlePosition === 'above' || titlePosition === 'before') &&
        (clearButton != null || inheritedIcon != null || labelElement != null)
    ) {
        titleElement = (
            <div className="parameter-field-title-element">
                {clearButton}
                {inheritedIcon}
                {labelElement}
            </div>
        );
    }

    let fieldStyle: React.CSSProperties;

    if (verticalSpaceBetweenFields != null && verticalSpaceBetweenFields > 0) {
        fieldStyle = {
            paddingBottom: verticalSpaceBetweenFields + 'px'
        };
    }

    if (options.height != null) {
        if (fieldStyle == null) {
            fieldStyle = {};
        }

        fieldStyle.height = options.height;
    }

    let content: React.JSX.Element;

    if (titlePosition === 'before') {
        const titleStyle: React.CSSProperties = {
            width: props.titleWidth != null && props.titleWidth > 0 ? props.titleWidth + 'px' : '20%'
        };

        content = (
            <table className="table-borderless w-100">
                <tbody>
                    <tr>
                        {titleElement && (
                            <td className="parameter-field-title-cell" style={titleStyle}>
                                {titleElement}
                            </td>
                        )}

                        <td className={'parameter-field-component-cell'}>{childElementsBlock}</td>

                        {inlineHelp}
                    </tr>
                </tbody>
            </table>
        );
    } else {
        content = (
            <>
                {titleElement}
                {childElementsBlock}
                {inlineHelp}
            </>
        );
    }

    return (
        <div
            className={
                'form-group parameter-field' +
                (props.selectMode ? ' select-mode' : '') +
                (props.locked ? ' locked' : '') +
                (props.className ? ' ' + props.className : '') +
                (options.inline ? ' inline' : '') +
                (options.inlineFixed != null ? ' inline-fixed' : '') +
                (props.hidden === true ? ' hidden' : '')
            }
            style={fieldStyle}
            onClick={props.onClick}
        >
            {overlay}
            {content}
        </div>
    );
}
